import {
  XyDataSeries,
  HorizontalLineAnnotation,
  EAnnotationLayer,
  SciChartSurface,
  ZoomPanModifier,
  MouseWheelZoomModifier,
  ZoomExtentsModifier,
  RolloverModifier,
  RubberBandXyZoomModifier,
  EXyDirection,
  EExecuteOn,
  FastLineRenderableSeries,
  FastImpulseRenderableSeries,
  LegendModifier,
  EAutoRange,
  NumberRange,
  EAxisAlignment,
  NumericAxis,
  DateTimeNumericAxis,
  CursorModifier,
  IRenderableSeries,
  IDataSeries,
  ISciChartSurfaceBase,
  AUTO_COLOR,
  UpdateSuspender,
  TrianglePointMarker,
} from "scichart";
import { IInitResult, TInitFunction } from "scichart-react";
import { ColumnPaletteProviderActivityChartAsset } from "../PaletteProvider";
import moment from "moment";
import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";

export interface TAssetActivityFlowRateChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
  setAutoRange: (value: boolean) => void,
  changeChartType: (viewType: string) => void,
  changeDataSeries: (visibleDataSeries: any) => void
}

export type TAssetActivityFlowRateConfigFunc = TInitFunction<SciChartSurface, TAssetActivityFlowRateChartConfigResult<SciChartSurface>>;

export interface Props {
  chartData: any
}

export interface ChartOptions {
  isAutoRange: boolean;
  isBarChartOn: boolean;
  visibleRange: { min: number | undefined; max: number | undefined; };
  setVisibleRange: (range: any) => void;
  setVisibleSeriesAsset: (series: any, isChecked: boolean) => void;
  visibleSeriesAsset: { [key: string]: boolean;};
  aggregator: string;
  viewType: string;
  visibleDataSeries: {
    volume?: boolean;
    volumeTemperatureCompensated?: boolean;
    flowRate?: boolean;
    flowRateTemperatureCompensated?: boolean;
    temperature?: boolean;
  };
  isFullscreen?: boolean;
}

export const getChartAPI = (props: Props, chartOptions: ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

  const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
  const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

  const theme = GetChartTheme();

  const tableau20 = [
    "#7B2CBF", // 6 Deep Purple
    "#0066FF", // 1 Bold Blue
    "#FF0000", // 2 Strong Red
    "#00CC00", // 3 Pure Green
    "#FF00CC", // 4 Magenta
    "#00DDFF", // 5 Sky Cyan
    "#000000",  // Black
    "#FF8800", // 7 Tangerine
    "#004D99", // 8 Navy Blue
    "#CC00FF", // 9 Vivid Violet
    "#00FF66", // 10 Mint Green
    "#FF0066", // 11 Hot Pink
    "#66FF00", // 13 Lime Green
    "#0099CC", // 14 Aqua Blue
    "#CC0000", // 15 Deep Red
    "#666666", // 16 Bold Gray
    "#6A1B9A", // 17 Dark Purple
    "#FFCC00", // 18 Golden Yellow
    "#3366FF", // 19 Bright Blue
    "#228B22", // 20 Forest Green
  ];
  
  theme.strokePalette = tableau20;
  theme.fillPalette = tableau20;  

  let setAutoRangeCallback: (arg0: boolean) => void;
  let changeChartTypeCallback: (arg0: string) => void;
  let changeDataSeriesCallback: (arg0: any) => void;

  const configAfterInit = (initResult: TAssetActivityFlowRateChartConfigResult<SciChartSurface>) => {
    setAutoRangeCallback = initResult.setAutoRange;
    changeChartTypeCallback = initResult.changeChartType;
    changeDataSeriesCallback = initResult.changeDataSeries;
  } 

  const setAutoRange = (value: boolean) => {
    if (setAutoRangeCallback !== undefined) {
      setAutoRangeCallback(value);
    }
  }

  const changeChartType = (viewType: string) => {
    if (changeChartTypeCallback !== undefined) {
      changeChartTypeCallback(viewType);
    }
  }

  const changeDataSeries = (visibleDataSeries: any) => {
    if (changeDataSeriesCallback !== undefined) {
      changeDataSeriesCallback(visibleDataSeries);
    }
  }

  const initChart: TAssetActivityFlowRateConfigFunc = async (rootElement: string | HTMLDivElement) => {
    const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});

    // #region Setup the XAxis
    const xAxis = new DateTimeNumericAxis(wasmContext, { 
      drawMajorBands: false, 
      drawMinorGridLines: false, 
      drawMajorGridLines: false, 
      visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
      visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
      growBy: new NumberRange(0.05, 0.05),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
    });
   
    // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
    xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
   
    // Setting timezone to local system
    const updateSettings = (xAxis$: NumericAxis) => {
      const timezone = moment.tz.guess();
      xAxis$.tickProvider = new DateTickProvider(wasmContext, timezone);
      xAxis$.labelProvider = new SmartDateTZLabelProvider(timezone, true, currentLanguage?.localeId);
      const dateTzConverter = createDateTzConverter(timezone);
      xAxis$.labelProvider.formatCursorLabel = timestamp =>
        chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
    };
    updateSettings(xAxis);

    xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });

    sciChartSurface.xAxes.add(xAxis);
    // #endregion
    
    // #region Setup the YAxis
    // Volume axis (Right)
    const yAxisVolume = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Right, 
      drawMajorBands: false,
      drawMinorGridLines: false, 
      drawMajorGridLines: true, 
      labelPostfix: " L", 
      labelPrecision: 2,
      growBy: new NumberRange(0, 0.2),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      id: "volumeAxis",
      autoRange: EAutoRange.Always
    });

    // Flow Rate axis (Left)
    const yAxisFlowRate = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Left, 
      stackedAxisLength: "33%",
      drawMajorBands: false,
      drawMinorGridLines: false, 
      drawMajorGridLines: true, 
      labelPostfix: " L/min", 
      labelPrecision: 2,
      growBy: new NumberRange(0, 0.2),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      id: "flowRateAxis",
      autoRange: EAutoRange.Always
    });

    // Temperature axis (Left, stacked)
    const yAxisTemperature = new NumericAxis(wasmContext, {
      axisAlignment: EAxisAlignment.Left,
      stackedAxisLength: "33%",
      drawMajorBands: false,
      drawMinorGridLines: false, 
      drawMajorGridLines: true, 
      labelPostfix: " °C", 
      labelPrecision: 1,
      growBy: new NumberRange(0, 0.2),
      labelStyle: {fontFamily:"Roboto", fontSize:12},
      id: "temperatureAxis",
      // autoRange: EAutoRange.Always
    });

    // Add zero line annotations
    const zeroLineVolume = new HorizontalLineAnnotation({
      y1: 0,
      yAxisId: "volumeAxis",
      stroke: "gray",
      strokeThickness: 1,
      annotationLayer: EAnnotationLayer.BelowChart
    });
    
    const zeroLineFlowRate = new HorizontalLineAnnotation({
      y1: 0,
      yAxisId: "flowRateAxis",
      stroke: "gray",
      strokeThickness: 1,
      annotationLayer: EAnnotationLayer.BelowChart
    });

    sciChartSurface.yAxes.add(yAxisVolume);
    sciChartSurface.yAxes.add(yAxisFlowRate);
    sciChartSurface.yAxes.add(yAxisTemperature);
    sciChartSurface.annotations.add(zeroLineVolume);
    sciChartSurface.annotations.add(zeroLineFlowRate);
    // #endregion

    // #region Setup the Chart Modifiers
    const legend = new LegendModifier({
      placementDivId: chartOptions.isFullscreen ? "legend-asset-fullscreen" : "legend-asset",
      showCheckboxes: true,
      showSeriesMarkers: true,
      showLegend: true,
      isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
        const yAxisId = series.yAxisId || "volumeAxis";
        const yAxis = sciChartSurface.yAxes.getById(yAxisId);
        if (yAxis) {
          yAxis.autoRange = EAutoRange.Always;
        }
        chartOptions.setVisibleSeriesAsset(series.id, isChecked)  
      }
    });

    sciChartSurface.chartModifiers.add(legend);

    sciChartSurface.chartModifiers.add(
      new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
      new MouseWheelZoomModifier({xyDirection: EXyDirection.XDirection}),
      new ZoomExtentsModifier({xyDirection: EXyDirection.XyDirection}),
      new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
      new CursorModifier({
        showTooltip: false, 
        hitTestRadius: 10, 
        tooltipContainerBackground: "#424242", 
        axisLabelFill: "#424242", 
        id: "AssetModifierGroup",
        showYLine: true,
        showAxisLabels: true
      }),
      new RolloverModifier({
        hitTestRadius: 10, 
        showTooltip: true, 
        snapToDataPoint: true, 
        showRolloverLine: false, 
        id: "AssetRollOver",
        tooltipDataTemplate: (seriesInfo: any) => {
          const lines: string[] = [];
          lines.push(seriesInfo.seriesName);
          lines.push(`X: ${seriesInfo.formattedXValue}`);
          
          if (seriesInfo.series && seriesInfo.series.yAxisId) {
            const yValue = seriesInfo.formattedYValue;
            let axisLabel = '';
            if (seriesInfo.series.yAxisId === 'volumeAxis') {
              axisLabel = ' L';
            } else if (seriesInfo.series.yAxisId === 'flowRateAxis') {
              axisLabel = ' L/min';
            } else if (seriesInfo.series.yAxisId === 'temperatureAxis') {
              axisLabel = ' °C';
            }
            lines.push(`Y: ${yValue}${axisLabel}`);
          } else {
            lines.push(`Y: ${seriesInfo.formattedYValue}`);
          }
          
          return lines;
        }
      }),
    );
    
    // #endregion
    
    // #region Create Data Series and Setup RenderableSeries

    // Create a series that represents the entire filter period
    const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
      id: "FilterPeriodSeriesId", 
      isVisible: false,
      dataSeries: new XyDataSeries(wasmContext, {
        xValues: [startFilterDateUnix, endFilterDateUnix],
        yValues: [0, 0], 
        dataSeriesName: "filterPeriodSeries"
      }),
      yAxisId: "volumeAxis"
    });  

    sciChartSurface.renderableSeries.add(filterPeriodSeries);
    legend.includeSeries(filterPeriodSeries, false);

    // Create Volume series
    const createVolumeSeries = (id: string, viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
      let volumeSeries;
      
      switch(viewType) {
        case 'bar':
        case 'userBar':
          volumeSeries = new FastImpulseRenderableSeries(wasmContext, {
            stroke: AUTO_COLOR,
            fill: AUTO_COLOR,
            pointMarker: new TrianglePointMarker(wasmContext, {
              width: 12,
              height: 12,
              stroke: "transparent",
              fill: "transparent"
            }),
            paletteProvider: new ColumnPaletteProviderActivityChartAsset(),
            id: id,
            dataSeries: dataSeries,
            isVisible: isVisible,
            yAxisId: "volumeAxis"
          });
          break;
        case 'line':
        case 'userLine':
        default:
          volumeSeries = new FastLineRenderableSeries(wasmContext, {
            stroke: AUTO_COLOR,
            id: id,
            dataSeries: dataSeries,
            isVisible: isVisible,
            yAxisId: "volumeAxis",
            pointMarker: undefined
          });
          break;
      }

      volumeSeries.rolloverModifierProps.tooltipTextColor = 'White';
      volumeSeries.rolloverModifierProps.showRollover = true;
      volumeSeries.rolloverModifierProps.markerColor = 'transparent';

      return volumeSeries;
    };

    // Process chart data - expect data from getLocoFlowMeterChartData
    // Structure: dataChart: [{ timestamps: [], volume: [], flowRate: [] }]
    for (let i = 0; i < props.chartData.dataChart.length; i++) {
      const endpoint = props.chartData.dataChart[i];
      
      if (!endpoint || !endpoint.timestamps || !Array.isArray(endpoint.timestamps) || endpoint.timestamps.length === 0) {
        continue;
      }
      
      const endpointName = endpoint.endpointName || `Endpoint ${i + 1}`;
      const isMultiple = props.chartData.dataChart.length > 1;
      
      // Create series in order: Volume, Temperature, Flow Rate, Volume Temp Comp, Flow Rate Temp Comp
      
      // 1. Create Volume series (non-compensated)
      // Create series if data exists, visibility will be controlled by changeDataSeries
      if (endpoint.volume && Array.isArray(endpoint.volume) && endpoint.volume.length > 0) {
        const minLength = Math.min(endpoint.timestamps.length, endpoint.volume.length);
        const volumeDataSeries = new XyDataSeries(wasmContext, {
          xValues: endpoint.timestamps.slice(0, minLength),
          yValues: endpoint.volume.slice(0, minLength),
          dataSeriesName: isMultiple ? `${endpointName} [Volume] (L)` : "Volume (L)"
        });

        const volumeSeries = createVolumeSeries(
          `${endpoint.endpointId || i}_Volume`,
          chartOptions.viewType,
          true, // Always create visible, will be controlled by visibleDataSeries
          volumeDataSeries
        );

        sciChartSurface.renderableSeries.add(volumeSeries);
        // Set initial visibility based on visibleDataSeries
        const isVolumeVisible = !!(chartOptions.visibleDataSeries.volume);
        volumeSeries.isVisible = isVolumeVisible;
        legend.includeSeries(volumeSeries, isVolumeVisible);
      }

      // 2. Create Temperature series
      // Create series if data exists, visibility will be controlled by changeDataSeries
      if (endpoint.temperature && Array.isArray(endpoint.temperature) && endpoint.temperature.length > 0) {
        const minLength = Math.min(endpoint.timestamps.length, endpoint.temperature.length);
        const temperatureDataSeries = new XyDataSeries(wasmContext, {
          xValues: endpoint.timestamps.slice(0, minLength),
          yValues: endpoint.temperature.slice(0, minLength),
          dataSeriesName: isMultiple ? `${endpointName} [Temperature] (°C)` : "Temperature (°C)"
        });

        const temperatureSeries = new FastLineRenderableSeries(wasmContext, {
          stroke: AUTO_COLOR,
          strokeThickness: 2,
          yAxisId: "temperatureAxis",
          id: `${endpoint.endpointId || i}_Temperature`,
          dataSeries: temperatureDataSeries,
          isVisible: false // Will be set below based on visibleDataSeries
        });

        temperatureSeries.rolloverModifierProps.tooltipTextColor = 'White';
        temperatureSeries.rolloverModifierProps.showRollover = true;

        sciChartSurface.renderableSeries.add(temperatureSeries);
        // Set initial visibility based on visibleDataSeries
        const isTemperatureVisible = !!(chartOptions.visibleDataSeries.temperature);
        temperatureSeries.isVisible = isTemperatureVisible;
        legend.includeSeries(temperatureSeries, isTemperatureVisible);
      }

      // 3. Create Flow Rate series (non-compensated)
      // Create series if data exists, visibility will be controlled by changeDataSeries
      if (endpoint.flowRate && Array.isArray(endpoint.flowRate) && endpoint.flowRate.length > 0) {
        const minLength = Math.min(endpoint.timestamps.length, endpoint.flowRate.length);
        const flowRateDataSeries = new XyDataSeries(wasmContext, {
          xValues: endpoint.timestamps.slice(0, minLength),
          yValues: endpoint.flowRate.slice(0, minLength),
          dataSeriesName: isMultiple ? `${endpointName} [Flow Rate] (L/min)` : "Flow Rate (L/min)"
        });

        const flowRateSeries = new FastLineRenderableSeries(wasmContext, {
          stroke: AUTO_COLOR,
          strokeThickness: 2,
          yAxisId: "flowRateAxis",
          id: `${endpoint.endpointId || i}_FlowRate`,
          dataSeries: flowRateDataSeries,
          isVisible: false, // Will be set below based on visibleDataSeries
          pointMarker: undefined
        });

        flowRateSeries.rolloverModifierProps.tooltipTextColor = 'White';
        flowRateSeries.rolloverModifierProps.showRollover = true;

        sciChartSurface.renderableSeries.add(flowRateSeries);
        // Set initial visibility based on visibleDataSeries
        const isFlowRateVisible = !!(chartOptions.visibleDataSeries.flowRate);
        flowRateSeries.isVisible = isFlowRateVisible;
        legend.includeSeries(flowRateSeries, isFlowRateVisible);
      }

      // 4. Create Volume Temperature Compensated series
      // Create series if data exists, visibility will be controlled by changeDataSeries
      if (endpoint.volumeTemperatureCompensated && Array.isArray(endpoint.volumeTemperatureCompensated) && endpoint.volumeTemperatureCompensated.length > 0) {
        const minLength = Math.min(endpoint.timestamps.length, endpoint.volumeTemperatureCompensated.length);
        const volumeTempCompDataSeries = new XyDataSeries(wasmContext, {
          xValues: endpoint.timestamps.slice(0, minLength),
          yValues: endpoint.volumeTemperatureCompensated.slice(0, minLength),
          dataSeriesName: isMultiple ? `${endpointName} [Volume Temp Comp] (L)` : "Volume (Temp Comp) (L)"
        });

        const volumeTempCompSeries = createVolumeSeries(
          `${endpoint.endpointId || i}_VolumeTempComp`,
          chartOptions.viewType,
          true, // Always create visible, will be controlled by visibleDataSeries
          volumeTempCompDataSeries
        );

        sciChartSurface.renderableSeries.add(volumeTempCompSeries);
        // Set initial visibility based on visibleDataSeries
        const isVolumeTempCompVisible = !!(chartOptions.visibleDataSeries.volumeTemperatureCompensated);
        volumeTempCompSeries.isVisible = isVolumeTempCompVisible;
        legend.includeSeries(volumeTempCompSeries, isVolumeTempCompVisible);
      }

      // 5. Create Flow Rate Temperature Compensated series
      if (endpoint.flowRateTemperatureCompensated && Array.isArray(endpoint.flowRateTemperatureCompensated) && endpoint.flowRateTemperatureCompensated.length > 0) {
        const minLength = Math.min(endpoint.timestamps.length, endpoint.flowRateTemperatureCompensated.length);
        const flowRateTempCompDataSeries = new XyDataSeries(wasmContext, {
          xValues: endpoint.timestamps.slice(0, minLength),
          yValues: endpoint.flowRateTemperatureCompensated.slice(0, minLength),
          dataSeriesName: isMultiple ? `${endpointName} [Flow Rate Temp Comp] (L/min)` : "Flow Rate (Temp Comp) (L/min)"
        });

        const flowRateTempCompSeries = new FastLineRenderableSeries(wasmContext, {
          stroke: AUTO_COLOR,
          strokeThickness: 2,
          strokeDashArray: [5, 5], // Dashed line to differentiate from non-compensated
          yAxisId: "flowRateAxis",
          id: `${endpoint.endpointId || i}_FlowRateTempComp`,
          dataSeries: flowRateTempCompDataSeries,
          isVisible: false, // Will be set below based on visibleDataSeries
          pointMarker: undefined
        });

        flowRateTempCompSeries.rolloverModifierProps.tooltipTextColor = 'White';
        flowRateTempCompSeries.rolloverModifierProps.showRollover = true;

        sciChartSurface.renderableSeries.add(flowRateTempCompSeries);
        // Set initial visibility based on visibleDataSeries
        const isFlowRateTempCompVisible = !!(chartOptions.visibleDataSeries.flowRateTemperatureCompensated);
        flowRateTempCompSeries.isVisible = isFlowRateTempCompVisible;
        legend.includeSeries(flowRateTempCompSeries, isFlowRateTempCompVisible);
      }
    }

    // #endregion

    // #region Setup the API's
    const setAutoRange = (value: boolean) => {
      UpdateSuspender.using(sciChartSurface, () => {
        sciChartSurface.yAxes.asArray().forEach(axis => {
          axis.autoRange = value ? EAutoRange.Always : EAutoRange.Never;
        });
      });
    };

    const changeChartType = (viewType: string) => {
      UpdateSuspender.using(sciChartSurface, () => {
        const renderableSeriesLength = sciChartSurface.renderableSeries.asArray().length;

        for(let i = 0; i < renderableSeriesLength; i++) {
          const series = sciChartSurface.renderableSeries.get(i);

          // Only change chart type for volume series (those without _FlowRate or _Temperature in their ID)
          if (series.id !== "FilterPeriodSeriesId" && !series.id.includes('_FlowRate') && !series.id.includes('_Temperature')) {
            const newVolumeSeries = createVolumeSeries(series.id, viewType, series.isVisible, series.dataSeries);
            
            // Preserve the y-axis assignment, defaulting to volumeAxis if not set
            newVolumeSeries.yAxisId = series.yAxisId || "volumeAxis";
            
            // Update legend to include the new series with same visibility
            legend.includeSeries(newVolumeSeries, series.isVisible);
            
            sciChartSurface.renderableSeries.set(i, newVolumeSeries);
          }
        }
      });
    };

    const changeDataSeries = (visibleDataSeries: any) => {
      UpdateSuspender.using(sciChartSurface, () => {
        const renderableSeriesLength = sciChartSurface.renderableSeries.asArray().length;
        
        // Track which axes have visible series
        let hasVisibleVolumeSeries = false;
        let hasVisibleFlowRateSeries = false;
        let hasVisibleTempSeries = false;

        for (let i = 0; i < renderableSeriesLength; i++) {
          const series = sciChartSurface.renderableSeries.get(i);
          
          if (series.id === "FilterPeriodSeriesId") {
            continue;
          }

          let isVisible = false;
          
          // Check series type by ID - order matters! Check TempComp first before regular
          if (series.id.includes('_VolumeTempComp')) {
            isVisible = !!(visibleDataSeries?.volumeTemperatureCompensated);
            if (isVisible) hasVisibleVolumeSeries = true;
          } else if (series.id.includes('_Volume') && !series.id.includes('TempComp')) {
            isVisible = !!(visibleDataSeries?.volume);
            if (isVisible) hasVisibleVolumeSeries = true;
          } else if (series.id.includes('_FlowRateTempComp')) {
            isVisible = !!(visibleDataSeries?.flowRateTemperatureCompensated);
            if (isVisible) hasVisibleFlowRateSeries = true;
          } else if (series.id.includes('_FlowRate') && !series.id.includes('TempComp')) {
            isVisible = !!(visibleDataSeries?.flowRate);
            if (isVisible) hasVisibleFlowRateSeries = true;
          } else if (series.id.includes('_Temperature')) {
            isVisible = !!(visibleDataSeries?.temperature);
            if (isVisible) hasVisibleTempSeries = true;
          }

          series.isVisible = isVisible;

          if (legend) {
            legend.includeSeries(series, isVisible);
          }
        }

        // Update axis visibility based on series visibility
        const volumeAxis = sciChartSurface.yAxes.getById("volumeAxis");
        const flowRateAxis = sciChartSurface.yAxes.getById("flowRateAxis");
        const temperatureAxis = sciChartSurface.yAxes.getById("temperatureAxis");

        if (volumeAxis) {
          volumeAxis.isVisible = hasVisibleVolumeSeries;
        }
        if (flowRateAxis) {
          flowRateAxis.isVisible = hasVisibleFlowRateSeries;
        }
        if (temperatureAxis) {
          temperatureAxis.isVisible = hasVisibleTempSeries;
        }

        // After updating visibility, recalculate ranges
        setAutoRange(chartOptions.isAutoRange);
        
        // Force a redraw to ensure all changes are applied
        sciChartSurface.invalidateElement();
      });
    };

    return { sciChartSurface, setAutoRange, changeChartType, changeDataSeries };
  };

  return {initChart, configAfterInit, setAutoRange, changeChartType, changeDataSeries};
}
